import React, { useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { motion } from 'framer-motion';
import ParticleBackground from './ParticleBackground';
import GlobalStyles from '../styles/GlobalStyles';
import theme from '../styles/theme';
import { fadeIn } from '../styles/animations';

// Styled components for the login screen
const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding: ${({ theme }) => theme.space.xl};
`;

const LoginCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: ${({ theme }) => theme.radii.lg};
  padding: ${({ theme }) => theme.space.xl};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  width: 100%;
  max-width: 450px;
  z-index: 10;
  border: 1px solid ${({ theme }) => theme.colors.accent};
`;

const Title = styled.h1`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.space.lg};
  color: ${({ theme }) => theme.colors.primary};
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  margin-bottom: ${({ theme }) => theme.space.xl};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const InputGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.space.lg};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.space.xs};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: bold;
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.space.md};
  border-radius: ${({ theme }) => theme.radii.md};
  border: 2px solid ${({ theme, error }) => 
    error ? theme.colors.error : theme.colors.border};
  background: ${({ theme }) => theme.colors.bgLight};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.fontSizes.md};
  transition: ${({ theme }) => theme.transitions.normal};
  
  &:focus {
    outline: none;
    border-color: ${({ theme, error }) => 
      error ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme, error }) => 
      error ? `${theme.colors.error}40` : `${theme.colors.primary}40`};
  }
`;

const Button = styled.button`
  padding: ${({ theme }) => theme.space.md};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.radii.md};
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: bold;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.normal};
  
  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background: ${({ theme }) => theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin-top: ${({ theme }) => theme.space.xs};
  min-height: 20px;
`;

const Footer = styled.div`
  margin-top: ${({ theme }) => theme.space.xl};
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/**
 * LoginScreen Component
 * Provides password-based authentication for the AI Dojo Results app
 */
const LoginScreen = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Simulate a slight delay for better UX
    setTimeout(() => {
      if (password === 'AIDoJo2025') {
        onLogin(true);
      } else {
        setError('Incorrect password. Please try again.');
        setIsLoading(false);
      }
    }, 800);
  };
  
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      
      {/* Background particles */}
      <ParticleBackground celebration={false} />
      
      <LoginContainer>
        <LoginCard
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          <img
            src="/baxter_logo.png"
            alt="Baxter Logo"
            style={{
              width: '180px',
              marginBottom: '20px',
              display: 'block',
              margin: '0 auto 20px auto'
            }}
          />
          <Title>AI Dojo 2025</Title>
          <Subtitle>
            Enter the password to access competition results
          </Subtitle>
          
          <Form onSubmit={handleSubmit}>
            <InputGroup>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                error={!!error}
                placeholder="Enter password"
                autoFocus
              />
              <ErrorMessage>{error}</ErrorMessage>
            </InputGroup>
            
            <Button type="submit" disabled={isLoading || !password}>
              {isLoading ? 'Authenticating...' : 'Access Results'}
            </Button>
          </Form>
          
          <Footer>
            <p>© {new Date().getFullYear()} AI Dojo Competition</p>
          </Footer>
        </LoginCard>
      </LoginContainer>
    </ThemeProvider>
  );
};

export default LoginScreen;